import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  AlertController,
  LoadingController,
  ToastController,
} from '@ionic/angular';
import { Account } from 'src/models/account.model';
import { UsersService } from 'src/services/users.service';

@Component({
  selector: 'app-account-form',
  templateUrl: './account-form.component.html',
  styleUrls: ['./account-form.component.scss'],
})
export class AccountFormComponent implements OnInit {
  @Input() account: Account;
  @Output() accountUpdated = new EventEmitter<Account>();

  accountForm: FormGroup;
  validationMessages: { name: string; type: string; message: string }[];

  imageHostUrl = 'https://nextgenrodeomedia.blob.core.windows.net/images/';
  picUrl: string;

  popoverActive: boolean;
  imageChanged = false;
  newImage: any;
  picBlob: any;

  submitAttempt = false;

  constructor(
    private fb: FormBuilder,
    private alert: AlertController,
    private toast: ToastController,
    private loadingCtrl: LoadingController,
    private userService: UsersService,
  ) {}

  ngOnInit() {
    this.initForm();
  }

  async deactivate() {
    const error = await this.alert.create({
      header: 'Delete Account',
      message:
        'Are you sure you want to delete your account? If you are having issues with the app, please call <a href="tel:9032877852">903-287-7852</a> before deleting.',
      buttons: [
        {
          text: 'Delete',
          handler: async () => {
            const loading = await this.loadingCtrl.create({
              message: 'Deleting your account',
            });
            let success = false;
            try {
              await this.userService.deactivateAccount(this.account.id);
              success = true;
            } catch (err) {
              console.warn(err);
              this.alert
                .create({
                  header: 'Error',
                  message:
                    'Could not deactivate account. Please try again later.',
                  cssClass: 'error',
                })
                .then((x) => x.present());
            } finally {
              loading.dismiss();
            }

            if (success) {
              this.accountUpdated.emit(undefined);
            }
          },
        },
        {
          text: 'Cancel',
          handler: () => {},
        },
      ],
    });
    await error.present();
  }

  async save() {
    this.submitAttempt = true;
    if (!this.accountForm.valid) {
      let toastMessage = '<ul>';

      this.validationMessages.forEach((message) => {
        toastMessage += '<li>' + message.message + '</li>';
      });

      toastMessage += '</ul>';

      this.toast
        .create({
          header: 'Account Errors',
          message: toastMessage,
          cssClass: 'custom-error-toast',
          color: 'danger',
          duration: 3000,
          position: 'bottom',
        })
        .then((toast) => toast.present());
      return;
    }

    const loader = await this.loadingCtrl.create({
      message: 'Saving account...',
    });
    await loader.present();
    const formValue = this.accountForm.value;
    if (formValue.birthDate) {
      formValue.birthDate = new Date(formValue.birthDate).toISOString();
    }

    try {
      const data = await this.userService.updateAccount(formValue);
      // console.log({ updateResp: data });
      if ((data as any).message) {
        this.alert
          .create({
            header: 'Update Account Failed',
            message: (data as any).message,
          })
          .then((x) => x.present());
      } else {
        this.accountUpdated.emit(data);
        this.toast
          .create({
            message: 'Account updated!',
            position: 'bottom',
            duration: 1500,
          })
          .then((x) => x.present());
      }
    } finally {
      loader.dismiss();
    }
  }

  private initForm() {
    let birthDate = this.account.birthDate;
    if (birthDate) {
      birthDate = new Date(birthDate).toISOString();
      birthDate = birthDate.substring(0, birthDate.indexOf('T'));
    }

    if (this.account.profilePicFileName) {
      this.picUrl = this.imageHostUrl + this.account.profilePicFileName;
    }

    this.accountForm = this.fb.group({
      id: [this.account.id],
      emailAddress: [
        this.account.emailAddress,
        [
          Validators.required,
          Validators.pattern(
            '^[a-zA-Z0-9._]+[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+.[a-zA-Z]{2,4}$',
          ),
        ],
      ],
      firstName: [this.account.firstName, [Validators.required]],
      lastName: [this.account.lastName, [Validators.required]],
      phone: [
        this.account.phone,
        [Validators.required, Validators.minLength(10)],
      ],
      birthDate: [birthDate, Validators.required],
      male: [this.account.male, Validators.required],
      streetAddress: [this.account.streetAddress, Validators.required],
      city: [this.account.city, Validators.required],
      state: [this.account.state, Validators.required],
      zip: [this.account.zip, Validators.required],
      country: [this.account.country, Validators.required],
      profilePicFileName: [this.account.profilePicFileName],
    });

    this.validationMessages = [
      {
        name: 'emailAddress',
        type: 'required',
        message: 'Email Address is required.',
      },
      {
        name: 'emailAddress',
        type: 'pattern',
        message: 'Your email address has an incorrect format.',
      },
      {
        name: 'phone',
        type: 'required',
        message: 'A valid phone is required.',
      },
      {
        name: 'firstName',
        type: 'required',
        message: 'Firstname is required.',
      },
      { name: 'lastName', type: 'required', message: 'Lastname is required.' },
      {
        name: 'birthDate',
        type: 'required',
        message: 'Birthdate is required.',
      },
      { name: 'male', type: 'required', message: 'Gender is required.' },
      {
        name: 'streetAddress',
        type: 'required',
        message: 'Street is required.',
      },
      { name: 'city', type: 'required', message: 'City is required.' },
      { name: 'state', type: 'required', message: 'State is required.' },
      { name: 'zip', type: 'required', message: 'Zipcode is required.' },
      { name: 'country', type: 'required', message: 'Country is required.' },
    ];
  }
}
